import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { replace as Replace } from 'redux-first-history';

import { Pagination, Paper } from '@dev/ee-components';

import { ApplicationState } from '@/store';
import { EventLogByChannelNameStart } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import useMobile from '@/services/hooks/useMobile';
import { DEFAULT_OFFSET, DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { addParamToUrl, calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';

interface PaginationComponentInterface {
    channelName: string;
}

const PaginationComponent: FC<PaginationComponentInterface> = ({
    channelName,
}: PaginationComponentInterface): JSX.Element => {
    const { reports } = useSelector((state: ApplicationState) => state);
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const totalCount = reports?.eventsByChannelNameCount ?? 0;
    const offset = reports?.eventLogParamsByChannelName?.offset || DEFAULT_OFFSET;
    const limit = reports?.eventLogParamsByChannelName?.limit || DEFAULT_PER_PAGE_COUNT;
    const activePage = calculateActivePage(offset, limit);

    const changePage = (page: number, perPage: number): void => {
        const offset = calculateOffset(page, perPage);
        const params: EventLogParamsByChannelName = {
            statuses: reports.eventLogParamsByChannelName?.statuses,
            offset,
            limit: perPage,
            channelName,
            messageCategory: reports.eventLogParamsByChannelName?.messageCategory,
            email: reports.eventLogParamsByChannelName?.email ? reports.eventLogParamsByChannelName?.email : null,
            from: reports.eventLogParamsByChannelName.from,
            to: reports.eventLogParamsByChannelName.to,
            lastEvent: reports.eventLogParamsByChannelName.lastEvent,
        };
        const newSearch = addParamToUrl({ perPage, page });
        dispatch(Replace({ search: newSearch }));
        dispatch(EventLogByChannelNameStart(params));
    };

    if (!reports.eventLogByChannelNameLoading && totalCount === 0) {
        return null;
    }

    const pagination = (
        <Pagination
            dataLength={totalCount}
            perPage={reports?.eventLogParamsByChannelName?.limit}
            activePage={activePage}
            handlePaginationChange={changePage}
            disableBorderTop={isMobile}
        />
    );

    if (isMobile) {
        return <Paper>{pagination}</Paper>;
    }
    return pagination;
};

export default PaginationComponent;
